import uiConfig from '@/config/ui';
import authService from '@/services/auth';
import router from '@/router';
import notificationService from '@/services/notification';
import Swal from '@/components/ui/Swal';

class InactivityService {
  constructor() {
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.warningShown = false;
    this.isLoggedIn = false;
    this.events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  }

  /**
   * Initialize inactivity tracking
   */
  init() {
    if (uiConfig.autoLogoutTimeoutMinutes <= 0) {
      return; // Auto logout disabled
    }

    this.isLoggedIn = authService.isAuthenticated();
    if (!this.isLoggedIn) {
      return; // Not logged in, no need to track
    }

    console.log(`Inactivity tracking enabled: ${uiConfig.autoLogoutTimeoutMinutes} minutes timeout`);
    this.addEventListeners();
    this.resetTimer();
  }

  /**
   * Add event listeners for user activity
   */
  addEventListeners() {
    this.events.forEach(event => {
      document.addEventListener(event, this.resetTimer.bind(this), true);
    });

    // Also listen for visibility change (tab switching)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    this.events.forEach(event => {
      document.removeEventListener(event, this.resetTimer.bind(this), true);
    });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  /**
   * Handle tab visibility change
   */
  handleVisibilityChange() {
    if (!document.hidden) {
      // Tab became visible, check if user is still logged in
      this.isLoggedIn = authService.isAuthenticated();
      if (this.isLoggedIn) {
        this.resetTimer();
      } else {
        this.cleanup();
      }
    }
  }

  /**
   * Reset the inactivity timer
   */
  resetTimer() {
    // Clear existing timers
    this.clearTimers();
    this.warningShown = false;

    if (!this.isLoggedIn || uiConfig.autoLogoutTimeoutMinutes <= 0) {
      return;
    }

    // Set warning timer (show warning X minutes before logout)
    const warningTime = (uiConfig.autoLogoutTimeoutMinutes - uiConfig.logoutWarningMinutes) * 60 * 1000;
    if (warningTime > 0) {
      this.warningTimeoutId = setTimeout(() => {
        this.showWarning();
      }, warningTime);
    }

    // Set logout timer
    const logoutTime = uiConfig.autoLogoutTimeoutMinutes * 60 * 1000;
    this.timeoutId = setTimeout(() => {
      this.performAutoLogout();
    }, logoutTime);
  }

  /**
   * Show warning dialog before auto logout
   */
  async showWarning() {
    if (this.warningShown || !this.isLoggedIn) {
      return;
    }

    this.warningShown = true;
    const remainingMinutes = uiConfig.logoutWarningMinutes;

    try {
      const result = await Swal.fire({
        title: 'Session Expiring',
        text: `You will be logged out in ${remainingMinutes} minute(s) due to inactivity. Click "Stay Logged In" to continue your session.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Stay Logged In',
        cancelButtonText: 'Logout Now',
        timer: remainingMinutes * 60 * 1000,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (result.isConfirmed) {
        // User chose to stay logged in
        this.resetTimer();
        notificationService.success.toast('Session extended successfully');
      } else if (result.isDismissed) {
        // User chose to logout or timer expired
        this.performAutoLogout();
      }
    } catch (error) {
      console.error('Error showing inactivity warning:', error);
      this.performAutoLogout();
    }
  }

  /**
   * Perform automatic logout
   */
  async performAutoLogout() {
    try {
      console.log('Performing auto logout due to inactivity');
      
      // Clear timers and remove listeners
      this.cleanup();
      
      // Show logout notification
      notificationService.warning.toast('You have been logged out due to inactivity');
      
      // Perform logout
      await authService.logout();
      
      // Redirect to login
      router.replace({ name: 'Login', query: { reason: 'inactivity' } });
      
    } catch (error) {
      console.error('Error during auto logout:', error);
      
      // Force logout even if API call fails
      authService.clearAuthData();
      router.replace({ name: 'Login', query: { reason: 'inactivity' } });
    }
  }

  /**
   * Clear all timers
   */
  clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  /**
   * Clean up timers and event listeners
   */
  cleanup() {
    this.clearTimers();
    this.removeEventListeners();
    this.isLoggedIn = false;
    this.warningShown = false;
  }

  /**
   * Start tracking (call when user logs in)
   */
  start() {
    this.isLoggedIn = true;
    this.init();
  }

  /**
   * Stop tracking (call when user logs out)
   */
  stop() {
    this.cleanup();
  }

  /**
   * Get remaining time until logout (in milliseconds)
   */
  getRemainingTime() {
    // This is an approximation since we don't track exact start time
    return uiConfig.autoLogoutTimeoutMinutes * 60 * 1000;
  }

  /**
   * Check if auto logout is enabled
   */
  isEnabled() {
    return uiConfig.autoLogoutTimeoutMinutes > 0;
  }
}

// Export singleton instance
export default new InactivityService();
